import { useCallback, useRef, type MutableRefObject } from 'react';
import axios from 'axios';
import type { AnalysisType, OrchestrateEvent, ParsedIntent, ToolResult, PermissionTier } from '@corner/shared';
import { ANALYSIS_TYPES } from '@corner/shared';
import type { ChatMessage, ProcessingState, ToolName } from '../types';
import { getFileFormatInfo } from '../lib/fileFormat';
import { api } from '../lib/api';
import { toast } from './use-toast';
import { showAxiosErrorBanner } from '../lib/appErrorBanner';
import { getUserContext } from '../lib/apiClient';
import type { RightPanelSettings } from '../types';
import { settingsToToolParams } from '../lib/settingsToToolParams';
import type { ChefPlan } from '@corner/shared';

/** Compact field summary for esign context (sent to backend for follow-up questions). */
export interface EsignFieldSummary {
  label: string;
  type: string;
  party: string;
  page: number;
}

/** Context from the most recent esign attempt, passed to follow-up orchestrate calls. */
export interface EsignContext {
  status: string;
  errorMessage?: string;
  errorCode?: string;
  fileName?: string;
  fileType?: string;
  fieldCount?: number;
  timestamp: number;
  /** Fields that were signed/placed in the most recent run */
  signedFields?: EsignFieldSummary[];
  /** Fields still awaiting signature/filling */
  awaitingFields?: EsignFieldSummary[];
  /** The role the user signed as (e.g. 'agent', 'buyer') */
  signingRole?: string;
}

interface Options {
  getToken: () => string | null;
  getCurrentDocumentContext?: () => { documentId?: string; fileName?: string; mimeType?: string };
  onProcessingChange: (state: ProcessingState | null) => void;
  onResult: (result: ToolResult, tool: ToolName) => void;
  onEsignStatusResult?: (result: ToolResult) => void;
  onMessages: (updater: (prev: ChatMessage[]) => ChatMessage[]) => void;
  onClarify: (question: string) => void;
  onEsignInteractive: (parsed: ParsedIntent, file: File | undefined) => void;
  onPlanUnderstanding?: (text: string) => void;
  // Chain-of-Thought callbacks
  onCoTPlanning?: () => void;
  onCoTPlanReady?: (plan: ChefPlan) => void;
  onCoTStepStart?: (stepIndex: number, description?: string, category?: import('@corner/shared').StepCategory) => void;
  onCoTStepComplete?: (stepIndex: number, resultSummary?: string) => void;
  onCoTStepError?: (stepIndex: number, error: string, errorCode?: import('@corner/shared').CoTErrorCode, toolName?: string, retryable?: boolean) => void;
  onCoTEditBatch?: (stepIndex: number, editBatch: import('@corner/shared').EditBatch) => void;
  onCoTThinkingChunk?: (chunk: string) => void;
  onCoTClarificationStep?: (stepIndex: number, question: string, options?: string[], allowFreeInput?: boolean) => void;
  onCoTDone?: (answerText?: string, partialCompletion?: boolean) => void;
  onCoTError?: (message: string) => void;
  onCoTReset?: () => void;
  // CORNER_V2: Permission confirmation callback — fired when a tool step needs user acknowledgment.
  onPermissionConfirmNeeded?: (request: {
    toolName: string;
    description: string;
    permissionTier: PermissionTier;
    stepIndex: number;
  }) => void;
  orchestrateAssistantMessageIdRef?: MutableRefObject<string | null>;
}

async function runTool(
  tool: string,
  files: File[],
  params: Record<string, unknown>,
  token: string | null,
  currentDocumentContext?: { documentId?: string; fileName?: string; mimeType?: string },
): Promise<ToolResult> {
  const formData = new FormData();
  files.forEach((f) => formData.append('files', f));
  formData.append('tool', tool);
  formData.append('params', JSON.stringify({
    ...(currentDocumentContext?.documentId && !('currentDocumentId' in params) ? { currentDocumentId: currentDocumentContext.documentId } : {}),
    ...(currentDocumentContext?.fileName && !('currentDocumentName' in params) ? { currentDocumentName: currentDocumentContext.fileName } : {}),
    ...(currentDocumentContext?.mimeType && !('currentDocumentMimeType' in params) ? { currentDocumentMimeType: currentDocumentContext.mimeType } : {}),
    ...params,
  }));

  const headers: Record<string, string> = { 'Content-Type': 'multipart/form-data' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await axios.post<ToolResult>('/api/execute', formData, { headers });
  return res.data;
}

function addMessage(
  onMessages: Options['onMessages'],
  role: 'user' | 'corner',
  content: string,
  attachmentName?: string
) {
  onMessages((prev) => [
    ...prev,
    {
      id: crypto.randomUUID(),
      role,
      content,
      timestamp: Date.now(),
      attachmentName,
    },
  ]);
}

function readAxiosMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { error?: unknown; message?: unknown } | undefined;
    if (typeof data?.message === 'string') return data.message;
    if (typeof data?.error === 'string') return data.error;
  }
  return err instanceof Error ? err.message : fallback;
}

function summarizeStepResult(event: OrchestrateEvent): string | undefined {
  if (event.result?.editBatch) {
    const count = event.result.editBatch.edits.length;
    return `Proposed ${count} document edit${count === 1 ? '' : 's'}.`;
  }
  if (event.result?.message) return event.result.message;
  if (event.result?.fileName) return `Created ${event.result.fileName}.`;
  if (event.tool) return `${event.description ?? event.tool} completed.`;
  return undefined;
}

export function useIntent({ getToken, getCurrentDocumentContext, onProcessingChange, onResult, onEsignStatusResult, onMessages, onClarify, onEsignInteractive, onPlanUnderstanding, onCoTPlanning, onCoTPlanReady, onCoTStepStart, onCoTStepComplete, onCoTStepError, onCoTEditBatch, onCoTThinkingChunk, onCoTDone, onCoTError, onCoTReset, onPermissionConfirmNeeded, onCoTClarificationStep, orchestrateAssistantMessageIdRef: orchestrateAssistantMessageIdRefProp }: Options) {
  const execute = useCallback(
    async (message: string, file?: File) => {
      try {
        const token = getToken();
        const currentDocumentContext = getCurrentDocumentContext?.();
        const authH = token ? { Authorization: `Bearer ${token}` } : {};
        // 1. Call parse route
        const formatInfo = file ? getFileFormatInfo(file.name) : null;
        const { data: parsed } = await axios.post<ParsedIntent>('/api/parse', {
          message,
          fileContext: file
            ? { name: file.name, type: file.type, size: file.size }
            : undefined,
          ...(formatInfo ? {
            fileFormat: formatInfo.format,
            fileCategory: formatInfo.category,
            canOCR: formatInfo.canOCR,
            isImage: formatInfo.isImage,
          } : {}),
        }, { headers: authH });

        // 2. Confidence gate — if low, show clarification and stop
        if (parsed.confidence < 0.7) {
          const question =
            parsed.clarification ?? "Could you clarify what you'd like to do?";
          addMessage(onMessages, 'corner', question);
          onClarify(question);
          onProcessingChange(null);
          return;
        }

        // 3. Intercept esign interactive — hand off to canvas, don't execute yet
        // Entry copy + metrics come from App (system line once PDF metadata is ready).
        const parsedPrimaryStep =
          parsed.steps?.length > 0
            ? parsed.steps[0]
            : parsed.tool
              ? {
                  tool: parsed.tool,
                  params: (parsed.params?.options ?? {}) as Record<string, unknown>,
                }
              : null;

        if (
          parsedPrimaryStep?.tool === 'sign_document'
          && file
          && (
            !(parsedPrimaryStep.params as Record<string, unknown> | undefined)?.fieldId
            || !(parsedPrimaryStep.params as Record<string, unknown> | undefined)?.documentId
          )
        ) {
          onProcessingChange(null);
          onEsignInteractive({ tool: 'esign', mode: 'interactive' } as unknown as ParsedIntent, file);
          return;
        }

        if (parsed.tool === 'esign' && parsed.mode === 'interactive' && file) {
          onProcessingChange(null);
          onEsignInteractive(parsed, file);
          return;
        }

        // 4. Analysis mode — call /analyze, add result to chat as AI message (no file tool)
        if (parsed.mode === 'analysis') {
          const analysisType = parsed.params?.analysisType;
          if (!analysisType || !ANALYSIS_TYPES.includes(analysisType)) {
            addMessage(onMessages, 'corner', parsed.clarification ?? 'Analysis type not recognized. Please try rephrasing.');
            onProcessingChange(null);
            return;
          }

          // 4a. Document draft — generate from scratch via /api/analyze/draft (no file needed)
          if (analysisType === 'document_draft') {
            onProcessingChange({ progress: 30, label: 'Writing document...' });
            try {
              const res = await axios.post<{ draftHtml: string; draftTitle: string; truncated?: boolean }>(
                '/api/analyze/draft',
                { prompt: message },
                { headers: token ? { Authorization: `Bearer ${token}` } : {} },
              );
              onProcessingChange(null);
              onResult({
                fileId: `draft-${Date.now()}`,
                downloadUrl: `draft://${Date.now()}`,
                fileName: res.data.draftTitle ?? 'Untitled Document',
                mimeType: 'text/html',
                sizeBytes: 0,
                draftHtml: res.data.draftHtml,
                draftTitle: res.data.draftTitle,
              }, 'extract_text');
              const draftMsg = res.data.truncated
                ? `I've drafted "${res.data.draftTitle}", but it was truncated due to length. Ask me to "continue the document" to generate the rest. You can also edit it directly.`
                : `I've drafted "${res.data.draftTitle}". You can edit it directly in the document view, or tell me what to change.`;
              addMessage(onMessages, 'corner', draftMsg);
            } catch (err) {
              onProcessingChange(null);
              const status = axios.isAxiosError(err) ? err.response?.status : undefined;
              const msg = status === 402
                ? 'You\u2019ve reached your plan limit for document generation. Upgrade to Pro for unlimited access.'
                : status === 429
                  ? 'Too many requests. Wait a moment and try again.'
                  : 'Failed to generate the document. Please try again.';
              addMessage(onMessages, 'corner', msg);
            }
            return;
          }

          if (!file) {
            addMessage(onMessages, 'corner', 'Please upload a document to analyze.');
            onProcessingChange(null);
            return;
          }
          addMessage(onMessages, 'corner', parsed.intent);
          onProcessingChange({ progress: 50, label: 'Analyzing...' });
          const data = await api.analyzeDocument(file, analysisType);
          onProcessingChange({ progress: 100, label: 'Done' });
          await new Promise((r) => setTimeout(r, 300));
          onProcessingChange(null);
          onMessages((prev) => [
            ...prev,
            {
              id: crypto.randomUUID(),
              role: 'corner',
              type: 'analysis' as const,
              content: data.result,
              analysisType: data.analysisType as AnalysisType,
              exportable: data.exportable,
              timestamp: Date.now(),
            },
          ]);
          return;
        }

        // 5. Confirm action in chat
        addMessage(onMessages, 'corner', parsed.intent);

        // 6. Build step list (single-step or multi-step)
        const steps =
          parsed.steps?.length > 0
            ? parsed.steps
            : [
                {
                  tool: parsed.tool,
                  params: (parsed.params?.options ?? {}) as Record<string, unknown>,
                  description: parsed.intent,
                },
              ];

        const firstStepParams = steps[0]?.params as Record<string, unknown> | undefined;
        if (
          steps[0]?.tool === 'sign_document'
          && (!firstStepParams?.fieldId || !firstStepParams?.documentId)
        ) {
          steps[0] = {
            ...steps[0],
            tool: 'esign',
            params: {},
          };
        }

        let currentFiles = file ? [file] : [];
        let lastResult: ToolResult | null = null;

        for (let i = 0; i < steps.length; i++) {
          const step = steps[i];
          if (!step.tool) {
            toast({ variant: 'destructive', title: 'Error', description: 'No tool selected for this step.' });
            onProcessingChange(null);
            return;
          }
          onProcessingChange({
            progress: Math.round(((i + 0.5) / steps.length) * 100),
            label: step.description,
            stepCurrent: steps.length > 1 ? i + 1 : undefined,
            stepTotal: steps.length > 1 ? steps.length : undefined,
          });

          lastResult = await runTool(
            step.tool,
            currentFiles,
            step.params as Record<string, unknown>,
            token,
            currentDocumentContext,
          );

          // For multi-step workflows: re-fetch output as a File for the next step
          if (i < steps.length - 1 && lastResult) {
            const resp = await fetch(lastResult.downloadUrl);
            const blob = await resp.blob();
            currentFiles = [
              new File([blob], lastResult.fileName, { type: lastResult.mimeType }),
            ];
          }
        }

        // 7. Done
        const lastToolName = steps[steps.length - 1].tool as ToolName;
        onProcessingChange({ progress: 100, label: 'Done' });
        await new Promise((r) => setTimeout(r, 300));
        onProcessingChange(null);

        if (lastResult) {
          onResult(lastResult, lastToolName);
        }
      } catch (err) {
        const msg = readAxiosMessage(err, 'Something went wrong');
        toast({ variant: 'destructive', title: 'Error', description: msg });
        onProcessingChange(null);
      }
    },
    [getToken, getCurrentDocumentContext, onProcessingChange, onResult, onMessages, onClarify, onEsignInteractive]
  );

  const rerunWithSettings = useCallback(
    async (tool: ToolName, file: File, settings: RightPanelSettings) => {
      try {
        onProcessingChange({ progress: 0, label: 'Re-running...' });
        const params = settingsToToolParams(tool, settings);
        const result = await runTool(tool, [file], params, getToken(), getCurrentDocumentContext?.());
        onProcessingChange({ progress: 100, label: 'Done' });
        await new Promise((r) => setTimeout(r, 200));
        onProcessingChange(null);
        onResult(result, tool);
      } catch (err) {
        const msg = readAxiosMessage(err, 'Re-run failed');
        toast({ variant: 'destructive', title: 'Re-run failed', description: msg });
        onProcessingChange(null);
      }
    },
    [getCurrentDocumentContext, onProcessingChange, onResult]
  );

  const plannedToolNamesRef = useRef<string[]>([]);
  const abortControllerRef = useRef<AbortController | null>(null);
  const thinkingMsgIdRef = useRef<string | null>(null);
  const thinkingAccRef = useRef<string>('');
  /** Matches SSE `assistant_placeholder` — consumed when building the final Corner message for this run. */
  const internalOrchestrateAssistantMessageIdRef = useRef<string | null>(null);
  const orchestrateAssistantMessageIdRef = orchestrateAssistantMessageIdRefProp ?? internalOrchestrateAssistantMessageIdRef;

  const stop = useCallback(() => {
    abortControllerRef.current?.abort();
  }, []);

  const executeWithOrchestrator = useCallback(
    async (message: string, files: File[], opts?: { esignContext?: EsignContext | null; selectedSnippet?: { text: string; page?: number; paragraphIndex?: number }; workingDocumentPlainText?: string }) => {
      abortControllerRef.current = new AbortController();
      // Combine manual abort with a 2-minute timeout to prevent indefinite hangs
      const timeoutId = setTimeout(() => abortControllerRef.current?.abort(), 120_000);
      const signal = abortControllerRef.current.signal;
      try {
        orchestrateAssistantMessageIdRef.current = null;
        plannedToolNamesRef.current = [];
        const formData = new FormData();
        formData.append('message', message);
        files.forEach((f) => formData.append('files', f));
        const currentDocumentContext = getCurrentDocumentContext?.();
        if (currentDocumentContext?.documentId) formData.append('currentDocumentId', currentDocumentContext.documentId);
        if (currentDocumentContext?.fileName) formData.append('currentDocumentName', currentDocumentContext.fileName);
        if (currentDocumentContext?.mimeType) formData.append('currentDocumentMimeType', currentDocumentContext.mimeType);
        if (opts?.esignContext) formData.append('esignContext', JSON.stringify(opts.esignContext));
        if (opts?.selectedSnippet) formData.append('selectedSnippet', JSON.stringify(opts.selectedSnippet));
        if (opts?.workingDocumentPlainText?.trim()) {
          formData.append('workingDocumentPlainText', opts.workingDocumentPlainText);
        }
        if (opts?.selectedSnippet) {
          console.log('[SelectionAction] orchestrate-request', {
            currentDocumentId: currentDocumentContext?.documentId ?? null,
            currentDocumentName: currentDocumentContext?.fileName ?? null,
            currentDocumentMimeType: currentDocumentContext?.mimeType ?? null,
            hasWorkingDocumentPlainText: Boolean(opts.workingDocumentPlainText?.trim()),
            selectedTextLength: opts.selectedSnippet.text.length,
          });
        }

        // Reset thinking stream state for this execution
        thinkingMsgIdRef.current = null;
        thinkingAccRef.current = '';

        const token = getToken();
        const fetchHeaders: Record<string, string> = {};
        if (token) fetchHeaders['Authorization'] = `Bearer ${token}`;
        // So App.tsx "idle" logic never sees mode=processing with processing=null
        // during the round-trip before the first SSE `planning` / `plan_ready` event.
        onProcessingChange({ progress: 2, label: 'Connecting…' });
        onCoTPlanning?.();
        const response = await fetch('/api/orchestrate', { method: 'POST', body: formData, signal, headers: fetchHeaders });
        if (!response.ok) {
          // Surface HTTP errors (429 rate limit, 500, etc.) through the app error banner
          let data: unknown;
          try { data = await response.json(); } catch { /* not JSON */ }
          showAxiosErrorBanner(response.status, data, getUserContext() ?? undefined);
          onCoTError?.('Corner could not start this run. Please try again.');
          onProcessingChange(null);
          return;
        }
        if (!response.body) throw new Error('No response body from orchestrator');

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let receivedTerminalEvent = false;
        let lastCompletedResult: ToolResult | null = null;
        let lastCompletedTool: string | undefined;
        let lastEditBatchStepIndex = 0;

        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const chunks = buffer.split('\n\n');
          buffer = chunks.pop() ?? '';

          for (const chunk of chunks) {
            if (!chunk.startsWith('data: ')) continue;
            const json = chunk.slice(6).trim();
            if (!json) continue;

            let event: OrchestrateEvent;
            try {
              event = JSON.parse(json) as OrchestrateEvent;
            } catch {
              continue; // skip malformed or split chunk
            }

            switch (event.type) {
              case 'assistant_placeholder': {
                const aid = event.assistantMessageId;
                if (aid) {
                  orchestrateAssistantMessageIdRef.current = aid;
                  onMessages((prev) => {
                    if (prev.some((m) => m.id === aid)) return prev;
                    return [
                      ...prev,
                      {
                        id: aid,
                        role: 'corner' as const,
                        content: '',
                        timestamp: Date.now(),
                        streaming: true,
                      },
                    ];
                  });
                }
                break;
              }
              case 'thinking_chunk': {
                if (onCoTThinkingChunk) {
                  onCoTThinkingChunk(event.chunk ?? '');
                  break;
                }
                // Stream Sous Chef reasoning into a live chat bubble
                if (!thinkingMsgIdRef.current) {
                  const id = crypto.randomUUID();
                  thinkingMsgIdRef.current = id;
                  thinkingAccRef.current = '';
                  onMessages((prev) => [
                    ...prev,
                    { id, role: 'corner' as const, content: '', timestamp: Date.now(), streaming: true },
                  ]);
                }
                thinkingAccRef.current += event.chunk ?? '';
                const msgId = thinkingMsgIdRef.current;
                const accumulated = thinkingAccRef.current;
                onMessages((prev) =>
                  prev.map((m) => (m.id === msgId ? { ...m, content: accumulated, streaming: true } : m))
                );
                break;
              }
              case 'planning':
                onProcessingChange({ progress: 5, label: 'Planning...' });
                onCoTPlanning?.();
                break;
              case 'plan_ready': {
                const toolNames = event.plan?.steps?.map((s) => s.toolName) ?? [];
                plannedToolNamesRef.current = toolNames;
                if (event.plan?.understanding) {
                  // Keep planner "understanding" out of the main chat; surface via an optional disclosure in the UI.
                  onPlanUnderstanding?.(event.plan.understanding);
                }
                // esign flows through the orchestrator like any other tool.
                // The step_complete handler renders the interactive UI from the result.
                if (toolNames.length > 0) {
                  onProcessingChange({
                    progress: 10,
                    label: event.plan?.steps?.[0]?.description ?? 'Starting...',
                    stepCurrent: 1,
                    stepTotal: toolNames.length,
                    toolNames,
                  });
                }
                if (event.plan) onCoTPlanReady?.(event.plan);
                break;
              }
              case 'clarification': {
                const ph = orchestrateAssistantMessageIdRef.current;
                orchestrateAssistantMessageIdRef.current = null;
                if (ph) {
                  onMessages((prev) => prev.filter((m) => m.id !== ph));
                }
                addMessage(onMessages, 'corner', event.question ?? 'Could you clarify?');
                onClarify(event.question ?? '');
                onProcessingChange(null);
                return;
              }
              case 'clarification_step':
                onCoTClarificationStep?.(
                  event.stepIndex ?? 0,
                  event.question ?? 'Could you clarify?',
                  event.options,
                  event.allowFreeInput,
                );
                break;
              case 'edit_batch':
                if (event.editBatch) {
                  lastEditBatchStepIndex = event.stepIndex ?? 0;
                  onCoTEditBatch?.(event.stepIndex ?? 0, event.editBatch);
                }
                break;
              case 'step_start': {
                // CORNER_V2: Permission confirmation — notify frontend when a step needs acknowledgment.
                // The SSE stream can't be paused, so this is informational for now.
                // When the backend adds pause-before-execute, this will gate actual execution.
                if (
                  (event.requiresConfirmation || event.permissionTier === 'Send') &&
                  event.tool &&
                  onPermissionConfirmNeeded
                ) {
                  onPermissionConfirmNeeded({
                    toolName: event.tool,
                    description: event.description ?? '',
                    permissionTier: (event.permissionTier ?? 'Apply') as PermissionTier,
                    stepIndex: event.stepIndex ?? 0,
                  });
                }

                // Fallback: build tool list from step_start when plan_ready had no steps
                if (event.tool) {
                  if (plannedToolNamesRef.current.length === 0) {
                    plannedToolNamesRef.current = [event.tool];
                  } else if (!plannedToolNamesRef.current.includes(event.tool)) {
                    plannedToolNamesRef.current = [...plannedToolNamesRef.current, event.tool];
                  }
                }
                const total = event.allSteps?.length ?? (event.stepIndex != null ? event.stepIndex + 1 : 1);
                onProcessingChange({
                  progress: Math.round(((event.stepIndex ?? 0) / Math.max(total, 1)) * 85) + 10,
                  label: event.description ?? `Running ${event.tool}`,
                  stepCurrent: (event.stepIndex ?? 0) + 1,
                  stepTotal: total,
                  toolNames: plannedToolNamesRef.current,
                });
                onCoTStepStart?.(event.stepIndex ?? 0, event.description, event.category);
                // Add a contextual processing message in chat (only for first step, skip when CoT is active)
                if ((event.stepIndex ?? 0) === 0 && !onCoTStepStart) {
                  const toolVerbs: Record<string, string> = {
                    pdf_to_word: 'converting your PDF to Word',
                    pdf_to_excel: 'converting your PDF to Excel',
                    pdf_to_pptx: 'converting your PDF to PowerPoint',
                    word_to_pdf: 'converting your document to PDF',
                    excel_to_pdf: 'converting your spreadsheet to PDF',
                    pptx_to_pdf: 'converting your presentation to PDF',
                    merge_pdf: 'merging your PDFs',
                    split_pdf: 'splitting your PDF',
                    compress_pdf: 'compressing your PDF',
                    compress_image: 'compressing your image',
                    rotate_pdf: 'rotating your PDF',
                    repair_pdf: 'repairing your PDF',
                    add_page_numbers: 'adding page numbers',
                    password_protect_pdf: 'encrypting your PDF',
                    remove_pdf_password: 'removing the password',
                    add_watermark_pdf: 'adding a watermark',
                    ocr: 'running OCR on your document',
                    remove_background: 'removing the background',
                    resize_image: 'resizing your image',
                    crop_image: 'cropping your image',
                    convert_image: 'converting your image',
                    generate_qr: 'generating your QR code',
                    extract_text: 'extracting text from your document',
                    esign: 'applying signatures',
                    summarize: 'summarizing your document',
                    study_questions: 'generating study questions',
                    key_terms: 'extracting key terms',
                    citation_generator: 'generating a citation',
                    contract_review: 'reviewing your contract',
                    action_items: 'extracting action items',
                    email_draft: 'drafting an email',
                    sensitive_data: 'scanning for sensitive data',
                    transcribe_audio: 'transcribing your audio',
                    extract_audio: 'extracting audio',
                    generate_document: 'generating your document',
                  };
                  const verb = toolVerbs[event.tool ?? ''] ?? 'processing your document';
                  addMessage(onMessages, 'corner', `On it — ${verb} now. This usually takes a few seconds…`);
                }
                break;
              }
              case 'step_complete':
                // Intermediate step done — step_start for the next step will update progress
                if (event.result) {
                  lastCompletedResult = event.result;
                  lastCompletedTool = event.tool;
                }
                if (event.tool === 'esign' && event.result?.status) {
                  onEsignStatusResult?.(event.result);
                }
                if (event.result?.editBatch) {
                  lastEditBatchStepIndex = event.stepIndex ?? lastEditBatchStepIndex;
                  onCoTEditBatch?.(event.stepIndex ?? 0, event.result.editBatch);
                }
                onCoTStepComplete?.(event.stepIndex ?? 0, summarizeStepResult(event));
                break;
              case 'step_error':
                if (!onCoTStepError) {
                  toast({
                    variant: 'destructive',
                    title: 'Step failed',
                    description: event.error ?? 'unknown error',
                  });
                }
                onCoTStepError?.(event.stepIndex ?? 0, event.error ?? 'Unknown error', event.errorCode, event.tool, event.retryable);
                break;
              case 'done': {
                receivedTerminalEvent = true;
                if (event.assistantMessageId) {
                  orchestrateAssistantMessageIdRef.current = event.assistantMessageId;
                }
                // Stop streaming cursor on thinking bubble, then remove it
                if (thinkingMsgIdRef.current) {
                  const idToRemove = thinkingMsgIdRef.current;
                  onMessages((prev) => prev.map((m) => m.id === idToRemove ? { ...m, streaming: false } : m));
                  await new Promise((r) => setTimeout(r, 80));
                  onMessages((prev) => prev.filter((m) => m.id !== idToRemove));
                  thinkingMsgIdRef.current = null;
                  thinkingAccRef.current = '';
                }
                // Detect partial completion: result exists but some steps failed
                const hasFailedSteps = event.allSteps?.some((s) => !s.success) ?? false;
                const isPartial = Boolean(event.finalResult) && hasFailedSteps;
                if (event.finalResult) {
                  const fr = event.finalResult;
                  if (!fr.textContent?.trim() && !fr.editBatch && (fr.fileId || fr.downloadUrl)) {
                  }
                }
                if (event.finalResult?.editBatch) {
                  const successfulEditStep = event.allSteps?.find((s) => s.success && s.result?.editBatch);
                  const idx = successfulEditStep?.stepIndex ?? lastEditBatchStepIndex;
                  onCoTEditBatch?.(idx ?? 0, event.finalResult.editBatch);
                }
                onCoTDone?.(event.finalResult?.textContent ?? undefined, isPartial);
                onProcessingChange({
                  progress: 100,
                  label: isPartial ? 'Partially complete' : 'Done',
                  toolNames: plannedToolNamesRef.current.length > 0 ? plannedToolNamesRef.current : undefined,
                });
                await new Promise((r) => setTimeout(r, 300));
                onProcessingChange(null);
                if (event.finalResult) {
                  const lastStep = event.allSteps?.filter((s) => s.success).pop();
                  onResult(event.finalResult, (lastStep?.toolName ?? 'convert_image') as ToolName);
                }
                break;
              }
              case 'error':
                receivedTerminalEvent = true;
                orchestrateAssistantMessageIdRef.current = null;
                if (!onCoTError) {
                  toast({
                    variant: 'destructive',
                    title: 'Error',
                    description: event.message ?? 'Orchestration failed',
                  });
                }
                onCoTError?.(event.message ?? 'Orchestration failed');
                onProcessingChange(null);
                break;
            }
          }
        }

        // Stream completed — clear the timeout guard
        clearTimeout(timeoutId);

        // Stream ended — if we never received a 'done' or 'error' event,
        // the server likely crashed mid-stream. Salvage a completed edit batch
        // from the last step_complete; otherwise clear the spinner so the user
        // isn't stuck waiting forever.
        if (thinkingMsgIdRef.current) {
          const idToRemove = thinkingMsgIdRef.current;
          onMessages((prev) => prev.filter((m) => m.id !== idToRemove));
          thinkingMsgIdRef.current = null;
          thinkingAccRef.current = '';
        }
        if (!receivedTerminalEvent) {
          if (lastCompletedResult?.editBatch) {
            onCoTEditBatch?.(lastEditBatchStepIndex, lastCompletedResult.editBatch);
            onCoTDone?.(undefined, true);
            onProcessingChange({
              progress: 100,
              label: 'Edits ready',
              toolNames: plannedToolNamesRef.current.length > 0 ? plannedToolNamesRef.current : undefined,
            });
            await new Promise((r) => setTimeout(r, 300));
            onProcessingChange(null);
            onResult(lastCompletedResult, (lastCompletedTool ?? 'plan_doc_edits') as ToolName);
          } else {
            onCoTError?.('The connection closed before Corner sent a final update. Your document context is still intact; please try the request again.');
            toast({ variant: 'destructive', title: 'Connection lost', description: 'The server stopped responding before sending a final update. Please try again.' });
            onProcessingChange(null);
          }
        } else if (thinkingMsgIdRef.current) {
          onProcessingChange(null);
        }
      } catch (err) {
        clearTimeout(timeoutId);
        if (err instanceof Error && err.name === 'AbortError') {
          orchestrateAssistantMessageIdRef.current = null;
          onProcessingChange(null);
          return;
        }
        const msg = err instanceof Error ? err.message : 'Orchestration failed';
        orchestrateAssistantMessageIdRef.current = null;
        toast({ variant: 'destructive', title: 'Error', description: msg });
        onProcessingChange(null);
      } finally {
        if (abortControllerRef.current?.signal === signal) {
          abortControllerRef.current = null;
        }
      }
    },
    [getCurrentDocumentContext, onProcessingChange, onResult, onEsignStatusResult, onMessages, onClarify, onEsignInteractive, onCoTPlanning, onCoTPlanReady, onCoTStepStart, onCoTStepComplete, onCoTStepError, onCoTEditBatch, onCoTThinkingChunk, onCoTDone, onCoTError, onCoTReset, onPermissionConfirmNeeded, onCoTClarificationStep]
  );

  return { execute, rerunWithSettings, executeWithOrchestrator, stop, orchestrateAssistantMessageIdRef };
}
